
import { Product, Category } from "@/types/common";

// Database response type that matches what Supabase actually returns
export interface ProductFromDatabase {
  id: string;
  name: string;
  description: string | null;
  price: number;
  compare_at_price: number | null;
  image_url: string | null;
  gallery_images?: string[] | null;
  category_id: string | null;
  stock_quantity: number | null;
  sku: string | null;
  featured: boolean | null;
  status: string;
  weight: number | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
  url_slug?: string | null; // Added url_slug field
  slug?: string | null; // Alternative field name
  categories?: {
    id: string;
    name: string;
    description: string | null;
    image_url: string | null;
    parent_id: string | null;
    created_at: string;
    updated_at: string;
  } | null;
}

// Utility function to convert database product to our Product type
export const convertToProduct = (dbProduct: ProductFromDatabase): Product => {
  return {
    id: dbProduct.id,
    name: dbProduct.name,
    description: dbProduct.description,
    price: dbProduct.price,
    compare_at_price: dbProduct.compare_at_price,
    image_url: dbProduct.image_url,
    gallery_images: dbProduct.gallery_images,
    category_id: dbProduct.category_id,
    stock_quantity: dbProduct.stock_quantity,
    sku: dbProduct.sku,
    featured: dbProduct.featured,
    status: dbProduct.status as 'active' | 'inactive' | 'draft',
    weight: dbProduct.weight,
    tags: dbProduct.tags,
    created_at: dbProduct.created_at,
    updated_at: dbProduct.updated_at,
    slug: dbProduct.url_slug || dbProduct.slug, // Map url_slug to slug
    categories: dbProduct.categories ? {
      id: dbProduct.categories.id,
      name: dbProduct.categories.name,
      description: dbProduct.categories.description,
      image_url: dbProduct.categories.image_url,
      parent_id: dbProduct.categories.parent_id,
      created_at: dbProduct.categories.created_at,
      updated_at: dbProduct.categories.updated_at,
    } : undefined,
  };
};

// Validation function that maintains type safety
export const isValidProduct = (product: ProductFromDatabase): product is ProductFromDatabase => {
  return !!(
    product.id && 
    product.id !== 'null' && 
    product.id !== 'undefined' &&
    typeof product.id === 'string' &&
    product.name &&
    typeof product.price === 'number'
  );
};

// Type guard to check if a product is valid for navigation
export const isProductValidForNavigation = (product: Product): boolean => {
  return !!(
    product.id && 
    product.id !== 'null' && 
    product.id !== 'undefined' &&
    typeof product.id === 'string'
  );
};

// Utility to get safe product URL
export const getSafeProductUrl = (product: Product): string => {
  if (!isProductValidForNavigation(product)) {
    return '/products';
  }
  return `/product/${product.id}`;
};

// Utility to safely get category name
export const getCategoryName = (product: Product): string | null => {
  return product.categories?.name || null;
};

// Utility to check if product has discount
export const hasDiscount = (product: Product): boolean => {
  return !!(product.compare_at_price && product.compare_at_price > product.price);
};

// Utility to calculate discount percentage
export const getDiscountPercentage = (product: Product): number => {
  if (!hasDiscount(product)) return 0;
  return Math.round(((product.compare_at_price! - product.price) / product.compare_at_price!) * 100);
};

// Utility to generate URL-friendly slug from product name
export const generateSlugFromName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};
